import { Button, Grid, Header, Input, Popup } from '@mairistem/composants';
import { formatMontant, Icon, UserRole } from '@mairistem/finances-utils';
import React from 'react';
import StatutMontantSimulation from '../../../../constants/simulation/statutMontantSimulation';
import TypePeriode, {
	LIBELLES_PERIODE,
} from '../../../../constants/simulation/typePeriode';
import type MontantSimulation from '../../../../interfaces/simulation/montantSimulation';
import isInputDisabled from '../../../../utils/simulation/input';
import './MontantsSimulationModalContentHeader.css';

interface MontantsSimulationModalContentHeaderProps {
	// Gestionnaire en gestion par service sans aucun service rattache a la ligne
	aucunServiceRattache: boolean;
	creationLigneImpossible: boolean;
	isDemandeEnabled: boolean;
	loading: boolean;
	montantsSimulation: MontantSimulation[];
	onAddMontant: () => void;
	onOpenImport: () => void;
	onSearchChange: (value: string) => void;
	search: string;
	transfertCpta: boolean;
	typePeriode: TypePeriode;
	userRole: UserRole;
}

const MontantsSimulationModalContentHeader = ({
	aucunServiceRattache,
	creationLigneImpossible,
	isDemandeEnabled,
	loading,
	montantsSimulation,
	onAddMontant,
	onOpenImport,
	onSearchChange,
	search,
	transfertCpta,
	typePeriode,
	userRole,
}: MontantsSimulationModalContentHeaderProps) => {
	const {
		nombreDemandes,
		nombreAArbitrer,
		totalDemande,
		nombreRefusees,
		totalRefuse,
	} = montantsSimulation.reduce(
		(compteurs, montantSimulation) => {
			if (montantSimulation.statut === StatutMontantSimulation.REFUSE) {
				compteurs.nombreRefusees += 1;
				compteurs.totalRefuse += montantSimulation.demande ?? 0;
				return compteurs;
			}

			compteurs.nombreDemandes += 1;
			compteurs.totalDemande += montantSimulation.demande ?? 0;
			if (montantSimulation.statut === StatutMontantSimulation.A_ARBITRER) {
				compteurs.nombreAArbitrer += 1;
			}
			return compteurs;
		},
		{
			nombreAArbitrer: 0,
			nombreDemandes: 0,
			nombreRefusees: 0,
			totalDemande: 0,
			totalRefuse: 0,
		},
	);
	const texteDemandeS = `demande${nombreDemandes > 1 ? 's' : ''}`;
	const texteCompteurDemandes =
		nombreAArbitrer === nombreDemandes
			? `${nombreDemandes} ${texteDemandeS} à arbitrer`
			: `${nombreDemandes} ${texteDemandeS} dont ${nombreAArbitrer} à arbitrer`;

	const isCA = typePeriode === TypePeriode.CA_CFU;

	// Hors periode Demande, seuls les administrateurs et charges du pilotage
	// peuvent creer ou importer une demande : pour les autres, les boutons sont
	// masques (et non simplement desactives) et remplaces par une mention
	const creationReservee =
		typePeriode !== TypePeriode.DEMANDE && userRole < UserRole.CHARGE_PILOTAGE;

	// Une recherche active filtre la liste affichee : ajouter une demande dans
	// cet etat pretrait a confusion (la demande creee peut ne pas apparaitre
	// dans les resultats filtres), on desactive donc le bouton le temps de la
	// recherche
	const rechercheActive = search.trim().length > 0;

	// Au CA, il n'y a plus de statut : le total porte sur le reste a realiser
	// de toutes les lignes plutot que sur les seules demandes a arbitrer
	const totalRar = montantsSimulation.reduce(
		(total, montant) => total + (montant.rar ?? 0),
		0,
	);

	// Raison affichee au survol quand l'ajout ou l'import est bloque faute de
	// service du Gestionnaire rattache a la ligne
	const motifAucunServiceRattache = aucunServiceRattache
		? "Aucun de vos services n'est rattaché à cette ligne budgétaire"
		: null;

	// Raison affichee au survol quand l'ajout est bloque : aucun service
	// rattache, recherche en cours, ou (sans gestion des demandes, hors gestion
	// par service) ligne sans origine deja existante
	const popupAddMontant =
		motifAucunServiceRattache ??
		(rechercheActive
			? "Impossible d'ajouter une demande tant qu'une recherche est en cours"
			: creationLigneImpossible
				? 'Une seule ligne sans origine peut être créée'
				: null);

	// Blocages communs a l'ajout et a l'import
	const creationDisabled =
		isInputDisabled(transfertCpta, typePeriode, userRole, true) ||
		loading ||
		aucunServiceRattache;

	// Un bouton disabled ne declenche pas les evenements de survol necessaires
	// au Popup : on l'enrobe dans un span, qui lui reste sensible au survol
	const avecMotif = (bouton: React.ReactElement, motif: string | null) =>
		motif ? (
			<Popup
				content={motif}
				position="top center"
				trigger={<span>{bouton}</span>}
			/>
		) : (
			bouton
		);

	const addMontantButton = (
		<Button
			content={
				isCA || !isDemandeEnabled ? 'Ajouter une ligne' : 'Ajouter une demande'
			}
			data-testid="addMontantButton"
			disabled={creationDisabled || rechercheActive || creationLigneImpossible}
			icon="plus"
			loading={loading}
			onClick={onAddMontant}
			positive
		/>
	);

	const importIdeeButton = (
		<Button
			content="Importer une idée Vision 360"
			data-testid="importIdeeButton"
			disabled={creationDisabled}
			icon="lightbulb outline"
			onClick={onOpenImport}
		/>
	);

	return (
		<Grid.Row>
			<Grid.Column verticalAlign="middle" width={5}>
				{isCA ? (
					<Header content={formatMontant(totalRar)} />
				) : (
					<>
						<div className="montantsSimulationModalContentHeaderCompteur">
							{texteCompteurDemandes}
							{nombreRefusees > 0 && (
								<Popup
									position="top center"
									wide
									content={
										<>
											<div>
												Les demandes refusées sont exclues de ce décompte et de
												ce total
											</div>
											<br />
											<div>
												{`${nombreRefusees} demande${nombreRefusees > 1 ? 's' : ''} refusée${nombreRefusees > 1 ? 's' : ''} ${formatMontant(totalRefuse)}`}
											</div>
										</>
									}
									trigger={
										<Icon
											className="montantsSimulationModalContentHeaderInfo"
											cursor="pointer"
											iconSet="Lucide"
											name="Info"
											size={14}
										/>
									}
								/>
							)}
						</div>
						<Header content={formatMontant(totalDemande)} />
					</>
				)}
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={5}>
				<Input
					data-testid="montantsSimulationSearch"
					fluid
					icon="search"
					iconPosition="left"
					input={{ 'data-testid': 'montantsSimulationSearchInput' }}
					onChange={(_e, { value }: { value: string }) => onSearchChange(value)}
					placeholder="Rechercher"
					value={search}
				/>
			</Grid.Column>
			<Grid.Column textAlign="right" verticalAlign="middle" width={6}>
				{creationReservee ? (
					<span data-testid="creationReserveeMention">
						{/* L'import n'existant pas en fin d'annee, la mention n'y parle que de la creation */}
						{`${isCA ? 'Création réservée' : 'Création et import réservés'} aux administrateurs et chargés du pilotage budgétaire en période ${LIBELLES_PERIODE[typePeriode]}`}
					</span>
				) : (
					<>
						{/* L'import d'idees Vision 360 n'existe pas en fin d'annee */}
						{!isCA && avecMotif(importIdeeButton, motifAucunServiceRattache)}
						{avecMotif(addMontantButton, popupAddMontant)}
					</>
				)}
			</Grid.Column>
		</Grid.Row>
	);
};

export default MontantsSimulationModalContentHeader;
